import React from "react";
import { motion } from "framer-motion";
import { RefreshCcw, AlertTriangle, MessagesSquare, ChevronRight, Clock, Check, X } from "lucide-react";
import { Link } from "react-router-dom";
import { PageLayout } from "../components/PageLayout";

const SECTIONS = [
  {
    id: "1",
    icon: RefreshCcw,
    tag: "Garantía",
    title: "1. Garantía de Devolución",
    body:
      "OptiShield X ofrece una garantía de reembolso de 48 horas tras la activación del servicio, aplicable exclusivamente a nuevos clientes en servicios de Game Servers y Bot Hosting.",
  },
  {
    id: "2",
    icon: AlertTriangle,
    tag: "Excepciones",
    title: "2. Excepciones",
    body:
      "Los reembolsos no son aplicables si el cliente ha violado nuestros Términos de Servicio (por ejemplo, al ejecutar scripts maliciosos, minería de criptomonedas o realizar ataques de denegación de servicio).",
  },
  {
    id: "3",
    icon: MessagesSquare,
    tag: "Proceso",
    title: "3. Proceso de Solicitud",
    body:
      "Para solicitar un reembolso dentro del periodo válido, debés abrir un ticket de soporte a través de nuestra comunidad de Discord oficial o mediante nuestro panel de facturación, explicando el motivo de tu insatisfacción.",
  },
];

const ELIGIBILITY = [
  { ok: true, label: "Game Servers Minecraft" },
  { ok: true, label: "Bot Hosting" },
  { ok: false, label: "VPS Linux" },
  { ok: false, label: "Servidores Dedicados" },
];

export const ReembolsosPage = () => {
  return (
    <PageLayout title="Política de Reembolsos | OptiShield X" showBackgroundOrbs>
      <section className="relative osx-noise">
        <div className="absolute inset-0 osx-grid-bg opacity-50" aria-hidden />
        <div className="osx-container relative pt-14 sm:pt-20 pb-10">
          <nav className="flex items-center gap-2 text-xs mb-4" style={{ color: "var(--osx-text-muted)" }}>
            <Link to="/" className="hover:underline" style={{ color: "var(--osx-brand-600)" }}>Inicio</Link>
            <ChevronRight size={12} />
            <span>Legal</span>
            <ChevronRight size={12} />
            <span style={{ color: "var(--osx-text)" }}>Reembolsos</span>
          </nav>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-5"
            style={{ background: "var(--osx-surface-2)", border: "1px solid var(--osx-border-strong)", color: "var(--osx-brand-600)" }}
          >
            <RefreshCcw size={13} /> Política de reembolsos
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight"
            style={{ color: "var(--osx-text)" }}
            data-testid="reembolsos-headline"
          >
            Reembolsos <span className="osx-gradient-text">transparentes</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="mt-4 max-w-2xl text-base sm:text-lg"
            style={{ color: "var(--osx-text-muted)" }}
          >
            48 horas de garantía en servicios elegibles. Sin letra chica: si no quedás conforme dentro del plazo, te devolvemos el dinero.
          </motion.p>

          <div className="mt-7 grid sm:grid-cols-3 gap-4 max-w-2xl">
            <div
              className="p-4 rounded-xl flex items-center gap-3"
              style={{ background: "var(--osx-surface)", border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-1)" }}
            >
              <Clock size={20} style={{ color: "var(--osx-brand-600)" }} />
              <div>
                <div className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--osx-text-muted)" }}>Plazo</div>
                <div className="text-lg font-bold" style={{ color: "var(--osx-text)" }}>48 horas</div>
              </div>
            </div>
            <div
              className="p-4 rounded-xl flex items-center gap-3"
              style={{ background: "var(--osx-surface)", border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-1)" }}
            >
              <RefreshCcw size={20} style={{ color: "var(--osx-brand-600)" }} />
              <div>
                <div className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--osx-text-muted)" }}>Tipo</div>
                <div className="text-lg font-bold" style={{ color: "var(--osx-text)" }}>100% del pago</div>
              </div>
            </div>
            <div
              className="p-4 rounded-xl flex items-center gap-3"
              style={{ background: "var(--osx-surface)", border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-1)" }}
            >
              <MessagesSquare size={20} style={{ color: "var(--osx-brand-600)" }} />
              <div>
                <div className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--osx-text-muted)" }}>Canal</div>
                <div className="text-lg font-bold" style={{ color: "var(--osx-text)" }}>Ticket / Discord</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative pb-12">
        <div className="osx-container grid lg:grid-cols-3 gap-5 max-w-5xl">
          {SECTIONS.map((s, idx) => (
            <motion.article
              key={s.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
              whileHover={{ y: -4 }}
              className="relative p-6 sm:p-7 rounded-2xl osx-gradient-border"
              style={{
                background: "var(--osx-surface)",
                border: "1px solid var(--osx-border)",
                boxShadow: "var(--osx-shadow-1)",
              }}
              data-testid={`reembolsos-section-${s.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div
                  className="inline-flex h-12 w-12 items-center justify-center rounded-xl text-white"
                  style={{
                    background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))",
                    boxShadow: "0 10px 24px rgba(2,132,199,0.30)",
                  }}
                >
                  <s.icon size={20} strokeWidth={2.2} />
                </div>
                <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-md"
                  style={{ background: "var(--osx-surface-2)", color: "var(--osx-text-muted)", border: "1px solid var(--osx-border)" }}>
                  {s.tag}
                </span>
              </div>
              <h2 className="text-lg font-bold tracking-tight" style={{ color: "var(--osx-text)" }}>
                {s.title}
              </h2>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
                {s.body}
              </p>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="relative pb-24">
        <div className="osx-container max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5 }}
            className="rounded-2xl p-6 sm:p-8"
            style={{
              background: "var(--osx-surface)",
              border: "1px solid var(--osx-border-strong)",
              boxShadow: "var(--osx-shadow-1)",
            }}
          >
            <h3 className="text-xl sm:text-2xl font-bold tracking-tight mb-1" style={{ color: "var(--osx-text)" }}>
              ¿Qué servicios son elegibles?
            </h3>
            <p className="text-sm" style={{ color: "var(--osx-text-muted)" }}>
              La garantía de 48 horas aplica a planes entrantes de hosting compartido.
            </p>
            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              {ELIGIBILITY.map((e) => (
                <div
                  key={e.label}
                  className="flex items-center gap-3 p-3 rounded-xl"
                  style={{
                    background: "var(--osx-surface-2)",
                    border: "1px solid var(--osx-border)",
                  }}
                >
                  <span
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full"
                    style={{
                      background: e.ok ? "color-mix(in oklab, #10b981 18%, transparent)" : "color-mix(in oklab, #ef4444 14%, transparent)",
                      color: e.ok ? "#059669" : "#dc2626",
                      border: `1px solid ${e.ok ? "rgba(16,185,129,0.35)" : "rgba(239,68,68,0.3)"}`,
                    }}
                  >
                    {e.ok ? <Check size={14} /> : <X size={14} />}
                  </span>
                  <span className="text-sm font-semibold" style={{ color: "var(--osx-text)" }}>
                    {e.label}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>

          <div
            className="mt-10 p-6 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
            style={{
              background:
                "linear-gradient(135deg, color-mix(in oklab, var(--osx-brand-600) 12%, var(--osx-surface)), var(--osx-surface))",
              border: "1px solid var(--osx-border-strong)",
            }}
          >
            <div>
              <div className="text-base font-bold" style={{ color: "var(--osx-text)" }}>¿Querés pedir un reembolso?</div>
              <div className="text-sm mt-1" style={{ color: "var(--osx-text-muted)" }}>
                Abrí un ticket en el panel o escribinos directo por Discord.
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link
                to="/terminos"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
                style={{ background: "var(--osx-surface)", border: "1px solid var(--osx-border-strong)", color: "var(--osx-text)" }}
                data-testid="reembolsos-terminos-link"
              >
                Ver Términos
              </Link>
              <a
                href="https://discord.gg/ExE6pGa4df"
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white"
                style={{
                  background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))",
                  boxShadow: "0 10px 24px rgba(2,132,199,0.3)",
                }}
                data-testid="reembolsos-discord-link"
              >
                Discord
              </a>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};
